import { RefObject, useEffect } from 'react';
import ResizeObserver from 'resize-observer-polyfill';
import { CONSOLE_HEADER_RESIZE_EVENT } from '../constants';
import { throttle } from 'src/commons/utils/throttle';

// abstracted this function out for testability
export const createLayoutResizeObserver = (): ((
  entries: ResizeObserverEntry[],
) => void) => {
  let previousHeaderHeight: number | undefined;
  return (entries: ResizeObserverEntry[]): void => {
    const entry = entries[0];
    if (!entry) {
      return;
    }
    const entryStyle = window.getComputedStyle(entry.target);
    const padding =
      parseFloat(entryStyle.getPropertyValue('padding-top')) +
      parseFloat(entryStyle.getPropertyValue('padding-bottom'));
    const headerHeight = entry.contentRect.height + padding;
    if (previousHeaderHeight === headerHeight) {
      return;
    }
    previousHeaderHeight = headerHeight;
    if (headerHeight) {
      document.dispatchEvent(
        new CustomEvent(CONSOLE_HEADER_RESIZE_EVENT, {
          detail: { headerHeight },
        }),
      );
    }
  };
};

/*
this hook is needed because the header, menu and page header are all
treated as individual elements that are fixed to the page so natural
layout cascading does no work here.  This hook will recalculate
the positioning of the menu and page header depending on the header
section.  The header section varies because there could be a console
level banner some times and we need to handle the case where the page
is loaded with a console level banner and then the user dismisses
the banner.
*/
export const useLayoutResizeObserver = ({
  consoleHeaderSectionRef,
}: {
  consoleHeaderSectionRef: RefObject<HTMLElement>;
}): void => {
  useEffect(() => {
    if (!consoleHeaderSectionRef.current) {
      throw new Error(
        'useLayoutResizeObserver failure - console header section not rendered.',
      );
    }
    const observer = new ResizeObserver(
      throttle(createLayoutResizeObserver(), 100),
    );
    observer.observe(consoleHeaderSectionRef.current);

    return () => {
      observer.disconnect();
    };
  }, [consoleHeaderSectionRef.current]);
};
